import { ComboboxData, ComboboxItem } from "@mantine/core";
import { DeepRequired } from "@mctypes/global.types";
import { IIssueBoardApi } from "@mcapps/shared";
import { IIssueApi, IIssueUpdateSchemaProps, IIssueUpdateUserSchemaProps, IUserPartial } from "@schemas/issueSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import {
  PutProjectByProjectIdIssueUpdateDescriptionApiArg,
  PutProjectByProjectIdIssueUpdateSimpleApiArg,
  PutProjectByProjectIdIssueUpdateUserApiArg,
} from "../../../../../../../openapi";
import { IssueDisabledColumnsEnum } from "../../../../../../mc-shared/enums";

export type CellCtx = {
  issue: IIssueApi;
  issueBoard: IIssueBoardApi;
  projectId: string;

  usersComboboxData: ComboboxItem[];
  multiUsersComboboxData: IUserPartial[];
  bucketOptions: ComboboxData;
  bucketReadOnly: boolean;
  typeOptions: ComboboxData;
  typeAheadPNSItems: string[];
  issueTags: string[];
  tagsLoading: boolean;
  projectMembers?: IProjectMemberApiItem[];

  updateSimple: (prop: IIssueUpdateSchemaProps) => Promise<void>;
  updateDescription: (prop: { description: string; descriptionRichText: string }) => Promise<void>;
  updateUser: (prop: IIssueUpdateUserSchemaProps) => Promise<void>;
};

export type CellDef = {
  /** Unique key for React list + ordering */
  id: string;
  /** If set, this cell can be hidden via IssueDisabledColumnsEnum[] */
  disableKey?: IssueDisabledColumnsEnum;

  span: number | ((ctx: CellCtx) => number);
  when?: (ctx: CellCtx) => boolean;
  render: (ctx: CellCtx) => React.ReactNode;
};

export async function guardedIssueUpdate<TProp extends Record<string, unknown>, TArg>({
  issue,
  prop,
  mutation,
  buildPayload,
}: {
  issue: IIssueApi;
  prop: TProp;
  mutation: (arg: TArg) => unknown;
  buildPayload: (prop: TProp) => TArg;
}) {
  const unchanged = Object.entries(prop).every(([key, newValue]) => {
    const oldValue = issue[key as keyof IIssueApi];
    return JSON.stringify(oldValue) === JSON.stringify(newValue);
  });

  if (unchanged) {
    return;
  }

  await mutation(buildPayload(prop));
}

export const TASK_TYPES: ComboboxData = [
  { label: "Opprettet", value: "Opprettet" },
  { label: "I prosess", value: "I prosess" },
  { label: "På hold", value: "På hold" },
  { label: "Fullført", value: "Fullført" },
  { label: "Utgår", value: "Utgår" },
];

export const PRIORITY_OPTIONS: ComboboxData = [
  { value: "HIGH", label: "Høy" },
  { value: "MEDIUM", label: "Middels" },
  { value: "LOW", label: "Lav" },
];

export const createSimplePayload =
  (projectId: string, issueId: string) =>
  (prop: IIssueUpdateSchemaProps): PutProjectByProjectIdIssueUpdateSimpleApiArg => ({
    projectId,
    body: {
      _id: issueId,
      property: prop as PutProjectByProjectIdIssueUpdateSimpleApiArg["body"]["property"],
    },
  });

export const createDescriptionPayload =
  (projectId: string, issueId: string) =>
  (prop: { description: string; descriptionRichText: string }): PutProjectByProjectIdIssueUpdateDescriptionApiArg => ({
    projectId,
    body: {
      _id: issueId,
      description: prop.description,
      descriptionRichText: prop.descriptionRichText,
    },
  });

export const createUserPayload =
  (projectId: string, issueId: string) =>
  (prop: DeepRequired<IIssueUpdateUserSchemaProps>): PutProjectByProjectIdIssueUpdateUserApiArg => ({
    projectId,
    body: {
      _id: issueId,
      property: prop,
    },
  });
